import { CheckCheck, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { NotificationCategory, NotificationSeverity } from "@/lib/notification.api";
import { ALL_FILTER_VALUE, CATEGORY_LABELS, SEVERITY_LABELS } from "../notification.constants";
import type { CategoryFilterValue, SeverityFilterValue } from "../hooks/useNotificationInboxState";

export function NotificationFilterBar({
  search,
  category,
  severity,
  unread,
  isFetching,
  markAllReadMutation,
  onSearchChange,
  onCategoryChange,
  onSeverityChange,
}: {
  search: string;
  category: CategoryFilterValue;
  severity: SeverityFilterValue;
  unread: number;
  isFetching: boolean;
  markAllReadMutation: {
    mutate: () => void;
    isPending: boolean;
  };
  onSearchChange: (value: string) => void;
  onCategoryChange: (value: CategoryFilterValue) => void;
  onSeverityChange: (value: SeverityFilterValue) => void;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-border p-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="grid flex-1 gap-2 sm:grid-cols-[minmax(220px,360px)_180px_170px]">
        <div className="relative">
          <Search
            size={13}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle"
          />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Rechercher une notification..."
            className="pl-8"
          />
        </div>

        <Select
          value={category}
          onValueChange={(value) => onCategoryChange(value as NotificationCategory | typeof ALL_FILTER_VALUE)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER_VALUE}>Toutes les catégories</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={severity}
          onValueChange={(value) => onSeverityChange(value as NotificationSeverity | typeof ALL_FILTER_VALUE)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Importance" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER_VALUE}>Toutes les priorités</SelectItem>
            {Object.entries(SEVERITY_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        {isFetching && (
          <span className="hidden items-center gap-1.5 text-[11px] text-subtle sm:flex">
            <Loader2 size={12} className="animate-spin" />
            Actualisation
          </span>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => markAllReadMutation.mutate()}
          disabled={unread === 0 || markAllReadMutation.isPending}
        >
          {markAllReadMutation.isPending ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <CheckCheck size={13} />
          )}
          Tout lire
        </Button>
      </div>
    </div>
  );
}
